import { useInfiniteQuery } from '@maui/react-query'
import { useMatiApiClient } from './client'

export const useExploreInfiniteQuery = (type, params, ids, limit = 50) => {
  const noFollowings = Array.isArray(ids) && !ids.length
  const config = configs[type]
  const body = transformParams(config, params, ids)
  const options = {
    ...body,
    limit,
    type,
    enabled: !noFollowings,
    enable_next_pagination: false,
  }
  const dashboardAPI = useMatiApiClient('dashboard')
  const queryKey = ids ? config.followingKey : config.dataKey
  return useInfiniteQuery({
    queryKey: [queryKey, { ...options }],
    queryFn: ({ pageParam: offset = 0 }) => {
      return dashboardAPI.getDashboardData({
        ...options,
        offset
      }).then(data => {
        data.offset = offset
        return adaptData(type, data)
      })
    },
    ...options,
    getNextPageParam: ({ offset, total_count: totalCount }) => {
      const nextPage = offset + limit
      if (totalCount <= nextPage) return
      return nextPage
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
}

export const transformParams = (config, params, ids) => {
  const sort = {}
  sort.sort_by = params.sortBy || config.defaultSort.sortBy
  sort.sort_order = params.sort || config.defaultSort.sort
  sort.sort_by_freemium = !!params.freemium
  const body = {
    ...sort,
  }
  body.filters = mergeFilters(config, params) || {}
  if (ids) {
    body.filters.ids = ids
  }
  if (params.search) {
    body.search = params.search
  }
  if (params.has_yara) {
    body.filters.has_yara = true
  }
  if (params?.malware_subtype) {
    body.filters.malware_subtype = [params.malware_subtype.toLowerCase()]
  }
  if (params?.campaign_type) {
    body.filters.campaign_type = [params.campaign_type.toLowerCase()]
  }
  if (config.dataKey === 'exploreReportsData') {
    const {
      exclude_vulnerability_report: vulnReport,
      filter_by_license: license,
    } = params
    body.exclude_vulnerability_report = vulnReport ? vulnReport === '1' : true
    body.filter_by_license = license ? license === '1' : true
    body.enable_next_pagination = false
  }
  if (params.start_epoch) {
    body.last_activity_time = { ...body.last_activity_time, start_epoch: parseInt(params.start_epoch) }
  }
  if (params.end_epoch) {
    body.last_activity_time = { ...body.last_activity_time, end_epoch: parseInt(params.end_epoch) }
  }
  if (params.topic_areas) {
    if (params.topic_areas === 'Info Ops & Hacktivism') {
      params.topic_areas = ['Info Ops', 'Hacktivism']
    } else if (params.topic_areas.includes('Info Ops & Hacktivism')) {
      const otherTopicAreas = params.topic_areas.filter(topicArea => topicArea !== 'Info Ops & Hacktivism')
      params.topic_areas = [...otherTopicAreas, 'Info Ops', 'Hacktivism']
    }
  }
  return body
}

export const mergeFilters = (config, params) => {
  const validFilters = config.filterOptions.map(filter => filter.name)
  const validNestedFilters = validateNestedFilters(config)
  const mergedFilters = [...validFilters, ...validNestedFilters]
  return mergedFilters.reduce((memo, name) => {
    return params[name]
      ? { ...memo, [name]: [params[name]].flat() }
      : memo
  }, {})
}

export const validateNestedFilters = (config) => {
  return config?.nestedFilterOptions
    ? Object.keys(config.nestedFilterOptions).map(filter => config.nestedFilterOptions[filter].map(({ name }) => name)).flat()
    : []
}

export const adaptData = (type, data) => {
  const key = {
    actor: 'actors',
    malware: 'malware',
    campaign: 'campaigns',
    report: 'reports',
  }[type]

  const { [key]: items, ...restData } = data || {}

  return {
    items,
    ...restData,
  }
}

const configs = {
  report: {
    dataKey: 'exploreReportsData',
    followingKey: 'exploreReportsFollowing',
    defaultSort: { sortBy: 'published_date', sort: 'desc' },
    sortOptions: [
      { label: 'Title - A to Z', sort: { sortBy: 'title', sort: 'asc' } },
      { label: 'Title - Z to A', sort: { sortBy: 'title', sort: 'desc' } },
      { label: 'Published Date - Least Recent', sort: { sortBy: 'published_date', sort: 'asc' } },
      { label: 'Published Date - Most Recent', sort: { sortBy: 'published_date', sort: 'desc' } },
    ],
    filterOptions: [
      {
        label: 'Topic Areas',
        name: 'topic_areas',
        vocab: 'topic_areas',
      },
      {
        label: 'Report Types',
        name: 'report_types',
        vocab: 'report_types',
      },
      {
        label: 'Target Regions',
        name: 'target_locations',
        vocab: 'target_regions'
      },
      {
        label: 'Source Region',
        name: 'source_locations',
        vocab: 'locations',
      },
      {
        label: 'Associated Industry',
        name: 'industries',
        vocab: 'industries',
      },
      {
        label: 'Actor',
        name: 'actors',
        vocab: 'actors',
      },
      {
        label: 'Malware',
        name: 'malware',
        vocab: 'malware',
      }
    ]
  },

  actor: {
    dataKey: 'exploreActorsData',
    followingKey: 'exploreActorsFollowing',
    defaultSort: { sortBy: 'last_activity', sort: 'desc' },
    dateOptions: ['start_epoch', 'end_epoch', 'range'],
    sortOptions: [
      { label: 'Last Seen - Least Recent', sort: { sortBy: 'last_activity', sort: 'asc' } },
      { label: 'Last Seen - Most Recent', sort: { sortBy: 'last_activity', sort: 'desc' } },
      { label: 'Name - A to Z', sort: { sortBy: 'name', sort: 'asc' } },
      { label: 'Name - Z to A', sort: { sortBy: 'name', sort: 'desc' } },
    ],
    filterOptions: [
      {
        label: 'Target Industry',
        name: 'target_industries',
      },
      {
        label: 'Target Region',
        name: 'target_locations',
      },
      {
        label: 'Source Region',
        name: 'source_locations',
      },
      {
        label: 'Associated Malware and Tools',
        name: 'malware',
      }
    ],
    nestedFilterOptions: {
      source_locations: [
        { name: 'source_regions' },
        { name: 'source_sub_regions' },
        { name: 'source_locations' },
      ]
    },
  },

  vulnerability: {
    dataKey: 'exploreVulnData',
    followingKey: 'exploreVulnFollowing',
    defaultSort: { sortBy: 'name', sort: 'desc' },
    sortOptions: [
      { label: 'CVE Identifier - A to Z', sort: { sortBy: 'name', sort: 'asc' } },
      { label: 'CVE Identifier - Z to A', sort: { sortBy: 'name', sort: 'desc' } },
    ],
    filterOptions: [
      {
        label: 'Risk Rating',
        name: 'risk_rating',
        options: [
          { label: 'Critical', value: 'critical' },
          { label: 'High', value: 'high' },
          { label: 'Medium', value: 'medium' },
          { label: 'Low', value: 'low' },
        ]
      },
      {
        label: 'Exploitation State',
        name: 'exploitation_state',
        vocab: 'exploitation_states'
      },
    ]
  },

  location: {
    dataKey: 'exploreLocationsData',
    followingKey: 'exploreLocationsFollowing',
    defaultSort: { sortBy: 'name', sort: 'asc' },
    sortOptions: [
      { label: 'Last Reference - Least Recent', sort: { sortBy: 'last_activity', sort: 'asc' } },
      { label: 'Last Reference - Most Recent', sort: { sortBy: 'last_activity', sort: 'desc' } },
      { label: 'Name - A to Z', sort: { sortBy: 'name', sort: 'asc' } },
      { label: 'Name - Z to A', sort: { sortBy: 'name', sort: 'desc' } },
    ],
    filterOptions: []
  },

  malware: {
    dataKey: 'exploreMalwareData',
    followingKey: 'exploreMalwareFollowing',
    defaultSort: { sortBy: 'last_activity', sort: 'desc' },
    dateOptions: ['start_epoch', 'end_epoch', 'range'],
    sortOptions: [
      { label: 'Last Seen - Least Recent', sort: { sortBy: 'last_activity', sort: 'asc' } },
      { label: 'Last Seen - Most Recent', sort: { sortBy: 'last_activity', sort: 'desc' } },
      { label: 'Name - A to Z', sort: { sortBy: 'name', sort: 'asc' } },
      { label: 'Name - Z to A', sort: { sortBy: 'name', sort: 'desc' } },
    ],
    filterOptions: [
      {
        label: 'Operating System',
        name: 'malware_os',
      },
      {
        label: 'Role',
        name: 'malware_role',
      },
      {
        label: 'Capabilities',
        name: 'malware_capabilities',
      }
    ]
  },

  campaign: {
    dataKey: 'exploreCampaignssData',
    followingKey: 'exploreCampaignsFollowing',
    freemiumSortNotice: 'Sorting Free Campaigns First - ',
    defaultSort: { sortBy: 'last_activity_time', sort: 'desc' },
    sortOptions: [
      { label: 'Published Date - Most Recent', sort: { sortBy: 'last_activity_time', sort: 'desc' } },
      { label: 'Published Date - Least Recent', sort: { sortBy: 'last_activity_time', sort: 'asc' } },
      { label: 'Name - A to Z', sort: { sortBy: 'name', sort: 'asc' } },
      { label: 'Name - Z to A', sort: { sortBy: 'name', sort: 'desc' } },
    ],
    filterOptions: [
      {
        label: 'Source Region',
        name: 'source_locations',
      },
      {
        label: 'Target Industry',
        name: 'target_industries',
      },
      {
        label: 'Target Region',
        name: 'target_locations',
      },
    ],
    nestedFilterOptions: {
      source_locations: [
        { name: 'source_regions' },
        { name: 'source_sub_regions' },
        { name: 'source_locations' },
      ]
    }
  },
}
